import React, { useRef, useCallback } from "react"
import { Editor, EditorHandle } from "@/editor/Editor"
import { getChartToolData, getAiContentData } from "@/editor/editorConfig"
import { useUpdateQuarterlyReportContent } from "~/api/quarterly_reports"
import { ReportSection } from "~/api/communications"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import PdfPageWrapper from "./PdfPageWrapper"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"
import { usePdfView } from "./usePdfView"

const LpHighLevelKpis: React.FC = () => {
  const { communicationId } = useCommunicationContext()
  const { reportId, report, isLoading, error, mock } =
    useQuarterlyReportContext()
  const editorRef = useRef<EditorHandle>(null)
  const updateReportContent = useUpdateQuarterlyReportContent()
  const { showPdfView, pdfLoading, pdfUrl, togglePdfView, loadPdfView } =
    usePdfView({
      reportId,
      sectionName: ReportSection.LP_HIGH_LEVEL_KPIS,
      editorContent:
        report?.section_content?.lp_high_level_kpis_and_highlights
          ?.editor_content,
      mock,
    })

  const onChange = useCallback(async () => {
    const content = await editorRef?.current?.getContent()
    if (!content || !reportId) return

    updateReportContent.mutate({
      id: reportId,
      lp_highlevel_kpis_and_highlights_editorjs_content: content,
    })
  }, [reportId, updateReportContent])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading KPIs & Highlights...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  return (
    <QuarterlyReportLayout
      title="LP High Level KPIs & Highlights"
      subtitle={`Edit the KPIs and highlights for ${report.household_name}`}
      showEditorActions={true}
      reportId={reportId}
      showPdfView={showPdfView}
      pdfLoading={pdfLoading}
      pdfUrl={pdfUrl}
      onTogglePdfView={togglePdfView}
      breadcrumbProps={{
        householdName: report.household_name,
        householdPath: `/manage/communications/${communicationId}/quarterly_reports/${reportId}`,
        sectionName: ReportSection.LP_HIGH_LEVEL_KPIS,
      }}
    >
      <PdfPageWrapper
        sectionTitle="LP High Level KPIs & Highlights"
        onPdfViewClick={loadPdfView}
      >
        <Editor
          ref={editorRef}
          initialData={
            report.section_content?.lp_high_level_kpis_and_highlights
              ?.editor_content
          }
          aiContentData={getAiContentData(report)}
          chartToolData={getChartToolData(report)}
          onChange={onChange}
          reportId={reportId}
        />
      </PdfPageWrapper>
    </QuarterlyReportLayout>
  )
}

export default LpHighLevelKpis
